import { useState, useEffect, useCallback, useRef } from "react";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";
import Collapse from "@mui/material/Collapse";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// MD Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { lightDark, DARK } from "utils/lightDark";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API + auth
import { adminApi } from "services/api";
import { useAuth } from "context/AuthContext";

import {
  DAYS,
  MONTHS,
  toLocalDateStr,
  hoursOnDay,
  monthTotalHours,
  buildMonthlyRows,
  entryPayload,
  toCsv,
  hoursProblem,
  monthEntryCount,
} from "layouts/timesheet/timesheetLogic";

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function isSameDay(a, b) {
  return toLocalDateStr(a) === toLocalDateStr(b);
}

const PROJECT_COLORS = [
  "#667eea",
  "#764ba2",
  "#f093fb",
  "#f5576c",
  "#4facfe",
  "#00f2fe",
  "#43e97b",
  "#38f9d7",
  "#fa709a",
  "#fee140",
  "#a18cd1",
  "#fbc2eb",
  "#ffecd2",
  "#fcb69f",
  "#a1c4fd",
];

// Phone (< sm) tap targets. Each sm value is the size the component already has, so
// tablets/desktop are unchanged.
// Small MDButtons are 32px (2rem) tall; lift them to 36px.
const TAP_BUTTON_SX = { minHeight: { xs: 36, sm: "2rem" } };
// Small IconButtons are 28px; lift them to 36px.
// (Selects and header Tabs get their phone tap heights from the theme.)
const TAP_ICON_SX = { minWidth: { xs: 36, sm: "auto" }, minHeight: { xs: 36, sm: "auto" } };

// ─── sub-components ──────────────────────────────────────────────────────────

function PageHeader({ activeTab, onTabChange }) {
  return (
    <MDBox
      mx={2}
      mt={-3}
      py={3}
      px={{ xs: 2, sm: 3 }}
      sx={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "lg",
        boxShadow: "0 8px 24px rgba(102,126,234,0.5)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(45deg, rgba(255,255,255,0.05) 0%, transparent 100%)",
          pointerEvents: "none",
        },
      }}
    >
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <MDBox display="flex" alignItems="center" gap={2}>
          <MDBox
            sx={{
              width: 50,
              height: 50,
              background: "rgba(255,255,255,0.15)",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <Icon sx={{ color: "white", fontSize: 28 }}>schedule</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h5" fontWeight="bold" color="white">
              Timesheet
            </MDTypography>
            <MDTypography variant="body2" color="white" sx={{ opacity: 0.8 }}>
              Log your work hours and manage projects
            </MDTypography>
          </MDBox>
        </MDBox>

        <Tabs
          value={activeTab}
          onChange={(_, v) => onTabChange(v)}
          sx={{
            "& .MuiTab-root": { color: "rgba(255,255,255,0.6)", fontWeight: 600, fontSize: 13 },
            // The theme draws the selected tab as a white pill (a dark one in dark mode), so its
            // label needs contrasting text.
            "& .Mui-selected": { color: lightDark("#344767 !important", "#ffffff !important") },
            "& .MuiTabs-indicator": { backgroundColor: "white" },
          }}
        >
          <Tab label="Calendar" icon={<Icon>calendar_month</Icon>} iconPosition="start" />
          <Tab label="Projects" icon={<Icon>folder</Icon>} iconPosition="start" />
          <Tab label="Reports" icon={<Icon>bar_chart</Icon>} iconPosition="start" />
          <Tab label="My Timesheet" icon={<Icon>badge</Icon>} iconPosition="start" />
        </Tabs>
      </MDBox>
    </MDBox>
  );
}

// ─── CALENDAR TAB ─────────────────────────────────────────────────────────────
// The shown month lives in the page (year, month 0-11) so its entries are loaded for whichever
// month is on screen.
function CalendarTab({
  entries,
  projects,
  onDayClick,
  loading,
  isGlobalAdmin,
  users,
  targetUserId,
  onTargetUserChange,
  year,
  month,
  onMonthChange,
}) {
  const today = new Date();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => {
    const d = new Date(year, month - 1, 1);
    onMonthChange(d.getFullYear(), d.getMonth());
  };
  const nextMonth = () => {
    const d = new Date(year, month + 1, 1);
    onMonthChange(d.getFullYear(), d.getMonth());
  };

  // Expand range entries: an entry with endDate appears on every day in [date, endDate]
  const entriesByDay = {};
  entries.forEach((e) => {
    const start = new Date(e.date);
    const end = e.endDate ? new Date(e.endDate) : start;
    const cur = new Date(start);
    while (cur <= end) {
      const key = toLocalDateStr(cur);
      if (!entriesByDay[key]) entriesByDay[key] = [];
      entriesByDay[key].push(e);
      cur.setDate(cur.getDate() + 1);
    }
  });

  // Build cells: leading blanks + day numbers + trailing blanks to complete the last row
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const isToday = (d) =>
    d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const CELL_HEIGHT = { xs: 80, sm: 110 };
  // minmax(0, 1fr): a plain 1fr track grows to fit nowrap entry labels, which blew the
  // month out past the card (and the page) whenever a project name was long.
  const GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: { xs: 0.5, sm: 1 },
  };

  return (
    <MDBox p={{ xs: 2, sm: 3 }}>
      {/* Admin-only: whose calendar this is showing / who a new entry gets attributed to */}
      {isGlobalAdmin && (
        <MDBox mb={2}>
          <FormControl size="small" sx={{ minWidth: 240, width: { xs: "100%", sm: "auto" } }}>
            <InputLabel>Viewing / adding for</InputLabel>
            <Select
              value={targetUserId || "me"}
              label="Viewing / adding for"
              onChange={(e) => onTargetUserChange(e.target.value === "me" ? null : e.target.value)}
            >
              <MenuItem value="me">Me</MenuItem>
              {users.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.name || u.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MDBox>
      )}

      {/* Month navigation */}
      <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <IconButton onClick={prevMonth} sx={{ bgcolor: lightDark("grey.100", DARK.surfaceHover) }}>
          <Icon>chevron_left</Icon>
        </IconButton>
        <MDTypography variant="h5" fontWeight="bold">
          {MONTHS[month]} {year}
        </MDTypography>
        <IconButton onClick={nextMonth} sx={{ bgcolor: lightDark("grey.100", DARK.surfaceHover) }}>
          <Icon>chevron_right</Icon>
        </IconButton>
      </MDBox>

      {/* Day-of-week header */}
      <MDBox
        sx={{
          ...GRID_SX,
          mb: 1,
        }}
      >
        {DAYS.map((d) => (
          <MDBox key={d} sx={{ textAlign: "center", py: 0.75 }}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              sx={{ textTransform: "uppercase", fontSize: { xs: "0.6rem", sm: "0.7rem" } }}
            >
              {d}
            </MDTypography>
          </MDBox>
        ))}
      </MDBox>

      {/* Calendar grid — 7-column CSS grid, breaks into weeks automatically */}
      {loading ? (
        <MDBox display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </MDBox>
      ) : (
        <MDBox sx={GRID_SX}>
          {cells.map((day, idx) => {
            const dateKey = day
              ? `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
              : null;
            const dayEntries = dateKey ? entriesByDay[dateKey] || [] : [];
            const totalHours = dayEntries.reduce((sum, e) => sum + hoursOnDay(e, dateKey), 0);

            return (
              <MDBox key={idx}>
                {day ? (
                  <MDBox
                    onClick={() => onDayClick(new Date(year, month, day), dayEntries)}
                    sx={{
                      minHeight: CELL_HEIGHT,
                      p: { xs: 0.5, sm: 1 },
                      border: "1.5px solid",
                      borderColor: isToday(day)
                        ? "primary.main"
                        : lightDark("grey.200", DARK.border),
                      borderRadius: "10px",
                      cursor: "pointer",
                      bgcolor: isToday(day) ? "rgba(102,126,234,0.07)" : "background.paper",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        bgcolor: "rgba(102,126,234,0.1)",
                        borderColor: "primary.main",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(102,126,234,0.15)",
                      },
                      overflow: "hidden",
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      fontWeight={isToday(day) ? "bold" : "medium"}
                      sx={{
                        color: isToday(day) ? "primary.main" : "text.primary",
                        display: "block",
                        mb: 0.5,
                        fontSize: "0.75rem",
                      }}
                    >
                      {day}
                    </MDTypography>

                    {dayEntries.length > 0 && (
                      <MDBox>
                        {dayEntries.slice(0, 2).map((e, i) => (
                          <MDBox
                            key={i}
                            sx={{
                              bgcolor: e.project?.color || "#667eea",
                              borderRadius: "4px",
                              px: 0.5,
                              py: 0.25,
                              mb: 0.3,
                              overflow: "hidden",
                            }}
                          >
                            <MDTypography
                              sx={{
                                fontSize: "0.55rem",
                                color: "white",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: 1.3,
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              {e.projectName}
                            </MDTypography>
                          </MDBox>
                        ))}
                        {dayEntries.length > 2 && (
                          <MDTypography
                            sx={{ fontSize: "0.6rem", color: "text.secondary", fontWeight: 600 }}
                          >
                            {/* phones: "+1" — "+1 more" doesn't fit a ~40px cell */}+
                            {dayEntries.length - 2}
                            <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                              {" "}
                              more
                            </MDBox>
                          </MDTypography>
                        )}
                        {totalHours > 0 && (
                          // Below md this is the day's main "hours logged" signal, so it
                          // renders as a small tinted badge.
                          <MDTypography
                            sx={{
                              fontSize: { xs: "0.65rem", md: "0.6rem" },
                              // A lighter pink in dark mode: primary.main on the tinted badge is ~2.8:1.
                              color: lightDark("primary.main", "#f06292"),
                              fontWeight: 700,
                              display: "block",
                              background: { xs: "rgba(102,126,234,0.12)", md: "none" },
                              borderRadius: "4px",
                              textAlign: { xs: "center", md: "inherit" },
                              whiteSpace: "nowrap",
                              mt: { xs: 0.25, md: 0 },
                            }}
                          >
                            {totalHours}h
                          </MDTypography>
                        )}
                      </MDBox>
                    )}

                    {dayEntries.length === 0 && (
                      <MDBox
                        className="add-hint"
                        sx={{
                          display: { xs: "none", sm: "flex" },
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: 0,
                          transition: "opacity 0.15s",
                          mt: 1,
                        }}
                      >
                        <Icon sx={{ fontSize: "1rem", color: "primary.main" }}>add</Icon>
                      </MDBox>
                    )}
                  </MDBox>
                ) : (
                  <MDBox sx={{ minHeight: CELL_HEIGHT }} />
                )}
              </MDBox>
            );
          })}
        </MDBox>
      )}

      <MDBox mt={3} display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <MDTypography variant="caption" color="text">
          Legend:
        </MDTypography>
        {projects.slice(0, 6).map((p) => (
          <MDBox key={p._id} display="flex" alignItems="center" gap={0.5}>
            <MDBox sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: p.color }} />
            <MDTypography variant="caption" color="text">
              {p.name}
            </MDTypography>
          </MDBox>
        ))}
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <MDBox sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#667eea" }} />
          <MDTypography variant="caption" color="text">
            Other
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

// ─── ENTRY DIALOG ─────────────────────────────────────────────────────────────
function EntryDialog({
  open,
  onClose,
  date,
  dayEntries,
  projects,
  onSaved,
  onDeleted,
  targetUserId,
  targetUserName,
}) {
  const [formOpen, setFormOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState({ projectId: "other", description: "", hours: "", endDate: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Phones: the day list + entry form is long, so the dialog goes full-screen below sm.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  const dateStr = date ? toLocalDateStr(date) : "";

  const resetForm = () => setForm({ projectId: "other", description: "", hours: "", endDate: "" });

  // The dialog stays mounted between days: start every opening with no form showing, so an
  // Edit left open on one day can't be saved onto another.
  useEffect(() => {
    if (!open) return;
    setFormOpen(false);
    setEditEntry(null);
    resetForm();
    setError("");
  }, [open, date]);

  const openNew = () => {
    resetForm();
    setEditEntry(null);
    setFormOpen(true);
    setError("");
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      projectId: entry.project?._id || "other",
      description: entry.description,
      hours: entry.hours || "",
      endDate: entry.endDate ? toLocalDateStr(entry.endDate) : "",
    });
    setFormOpen(true);
    setError("");
  };

  const handleSave = async () => {
    if (!form.description.trim()) {
      setError("Description is required");
      return;
    }
    const problem = hoursProblem(form, {
      startStr: editEntry ? toLocalDateStr(editEntry.date) : dateStr,
      editEntry,
    });
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = entryPayload(form, { editEntry, dateStr, targetUserId });
      if (editEntry) {
        await adminApi.updateTimesheetEntry(editEntry._id, payload);
      } else {
        await adminApi.createTimesheetEntry(payload);
      }
      setFormOpen(false);
      setEditEntry(null);
      resetForm();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await adminApi.deleteTimesheetEntry(id);
      onDeleted();
    } catch (err) {
      setError("Failed to delete entry");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth={!isPhone}
      fullScreen={isPhone}
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: "16px" } } }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          fontWeight: "bold",
        }}
      >
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          color="inherit"
        >
          <MDBox display="flex" alignItems="center" gap={1} color="inherit">
            <Icon>event</Icon>
            <span>
              {date
                ? date.toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })
                : ""}
            </span>
          </MDBox>
          <IconButton onClick={onClose} size="small" sx={{ color: "white", ...TAP_ICON_SX }}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {targetUserId && (
          <MDBox sx={{ px: 2, pt: 2 }}>
            <Alert severity="info" icon={<Icon fontSize="small">person</Icon>}>
              Adding entry for: <strong>{targetUserName}</strong>
            </Alert>
          </MDBox>
        )}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Existing entries for this day */}
        {dayEntries.length > 0 && (
          <MDBox px={2} pt={2}>
            <MDTypography
              variant="button"
              fontWeight="bold"
              color="text"
              sx={{ mb: 1, display: "block" }}
            >
              Logged entries
            </MDTypography>
            {dayEntries.map((entry) => (
              <MDBox
                key={entry._id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: lightDark("grey.200", DARK.border),
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                }}
              >
                <MDBox
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "3px",
                    flexShrink: 0,
                    mt: 0.5,
                    bgcolor: entry.project?.color || "#667eea",
                  }}
                />
                <MDBox flex={1} minWidth={0}>
                  <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    sx={{ display: "block" }}
                  >
                    {entry.projectName}
                    {entry.hours ? (
                      <span style={{ fontWeight: 400, marginLeft: 6, color: "#888" }}>
                        {entry.hours}h
                      </span>
                    ) : null}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text"
                    sx={{ opacity: 0.7, display: "block", wordBreak: "break-word" }}
                  >
                    {entry.description}
                  </MDTypography>
                  {entry.endDate && (
                    <MDTypography
                      variant="caption"
                      color="info"
                      sx={{ display: "block", fontSize: "0.65rem" }}
                    >
                      Until: {formatDate(entry.endDate)}
                    </MDTypography>
                  )}
                </MDBox>
                <MDBox display="flex" gap={0.5} flexShrink={0}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(entry)} sx={TAP_ICON_SX}>
                      <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(entry._id)}
                      sx={TAP_ICON_SX}
                    >
                      <Icon sx={{ fontSize: "1rem" }}>delete</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>
              </MDBox>
            ))}
            <Divider />
          </MDBox>
        )}

        {/* Add / edit form */}
        <Collapse in={formOpen}>
          <MDBox px={2} pt={2} pb={1}>
            <MDTypography
              variant="button"
              fontWeight="bold"
              color="text"
              sx={{ mb: editEntry ? 0.5 : 1.5, display: "block" }}
            >
              {editEntry ? "Edit entry" : "New entry"}
            </MDTypography>
            {editEntry && (
              <MDTypography variant="caption" color="text" sx={{ mb: 1.5, display: "block" }}>
                Starts {formatDate(editEntry.date)}
              </MDTypography>
            )}

            <MDBox mb={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Project</InputLabel>
                <Select
                  value={form.projectId}
                  label="Project"
                  onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
                >
                  <MenuItem value="other">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox
                        sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: "#667eea" }}
                      />
                      Other
                    </MDBox>
                  </MenuItem>
                  {projects
                    .filter((p) => p.active)
                    .map((p) => (
                      <MenuItem key={p._id} value={p._id}>
                        {/* minWidth 0 + ellipsis: a long project name truncates inside the field instead of being cut off */}
                        <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                          <MDBox
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "2px",
                              bgcolor: p.color,
                              flexShrink: 0,
                            }}
                          />
                          <MDBox
                            component="span"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {p.name}
                          </MDBox>
                        </MDBox>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </MDBox>

            <MDBox mb={2}>
              <TextField
                label="Description"
                multiline
                rows={3}
                fullWidth
                size="small"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What did you work on?"
              />
            </MDBox>

            <MDBox mb={2} display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
              <TextField
                label="Hours (optional)"
                type="number"
                size="small"
                inputProps={{ min: 0.5, step: 0.5 }}
                value={form.hours}
                onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                label="End date (optional)"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                inputProps={{ min: editEntry ? toLocalDateStr(editEntry.date) : dateStr }}
                sx={{ flex: 1 }}
              />
            </MDBox>

            <MDBox display="flex" gap={1} justifyContent="flex-end">
              <MDButton
                size="small"
                color="secondary"
                onClick={() => {
                  setFormOpen(false);
                  setEditEntry(null);
                }}
                sx={TAP_BUTTON_SX}
              >
                Cancel
              </MDButton>
              <MDButton
                size="small"
                variant="gradient"
                color="dark"
                onClick={handleSave}
                disabled={saving}
                sx={TAP_BUTTON_SX}
              >
                {saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : editEntry ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </MDButton>
            </MDBox>
          </MDBox>
        </Collapse>

        {!formOpen && (
          <MDBox px={2} py={2}>
            <MDButton
              fullWidth
              variant="outlined"
              color="dark"
              startIcon={<Icon>add</Icon>}
              onClick={openNew}
              sx={{ borderRadius: "10px", borderStyle: "dashed" }}
            >
              Add entry for this day
            </MDButton>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <MDButton onClick={onClose} color="secondary" size="small" sx={TAP_BUTTON_SX}>
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── PROJECTS TAB ─────────────────────────────────────────────────────────────
function ProjectsTab({ projects, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#667eea", active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const openNew = () => {
    setEditProject(null);
    setForm({ name: "", description: "", color: "#667eea", active: true });
    setError("");
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditProject(p);
    setForm({
      name: p.name,
      description: p.description || "",
      color: p.color || "#667eea",
      active: p.active,
    });
    setError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editProject) {
        await adminApi.updateTimesheetProject(editProject._id, form);
        setSuccess("Project updated");
      } else {
        await adminApi.createTimesheetProject(form);
        setSuccess("Project created");
      }
      setDialogOpen(false);
      onRefresh();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project? This won't delete existing timesheet entries."))
      return;
    try {
      await adminApi.deleteTimesheetProject(id);
      setSuccess("Project deleted");
      onRefresh();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError("Failed to delete project");
    }
  };

  return (
    <MDBox p={{ xs: 2, sm: 3 }}>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={3}
      >
        <MDTypography variant="h6" fontWeight="bold">
          Manage Projects
        </MDTypography>
        <MDButton variant="gradient" color="dark" startIcon={<Icon>add</Icon>} onClick={openNew}>
          New Project
        </MDButton>
      </MDBox>

      {projects.length === 0 ? (
        <MDBox textAlign="center" py={6}>
          <Icon sx={{ fontSize: 48, color: "grey.400" }}>folder_open</Icon>
          <MDTypography variant="body2" color="text" mt={1}>
            No projects yet. Create one to start tracking time.
          </MDTypography>
        </MDBox>
      ) : (
        <Grid container spacing={2}>
          {projects.map((p) => (
            <Grid item xs={12} sm={6} md={4} key={p._id}>
              <MDBox
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: "1.5px solid",
                  borderColor: lightDark("grey.200", DARK.border),
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: 5,
                    bgcolor: p.color,
                  }}
                />
                <MDBox pl={1.5}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                    <MDBox>
                      <MDTypography variant="button" fontWeight="bold" display="block">
                        {p.name}
                      </MDTypography>
                      {p.description && (
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{ opacity: 0.7, display: "block", mt: 0.5 }}
                        >
                          {p.description}
                        </MDTypography>
                      )}
                    </MDBox>
                    <Chip
                      label={p.active ? "Active" : "Inactive"}
                      color={p.active ? "success" : "default"}
                      size="small"
                    />
                  </MDBox>
                  <MDBox display="flex" gap={1} mt={2}>
                    <MDButton
                      size="small"
                      variant="outlined"
                      color="dark"
                      onClick={() => openEdit(p)}
                      sx={TAP_BUTTON_SX}
                    >
                      Edit
                    </MDButton>
                    <MDButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(p._id)}
                      sx={TAP_BUTTON_SX}
                    >
                      Delete
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Project dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {editProject ? "Edit Project" : "New Project"}
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <MDBox mb={2}>
            <MDInput
              label="Project name"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </MDBox>
          <MDBox mb={2}>
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </MDBox>

          <MDBox mb={2}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              color="text"
              sx={{ mb: 1, display: "block" }}
            >
              Project colour
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              {PROJECT_COLORS.map((c) => (
                <MDBox
                  key={c}
                  onClick={() => setForm((f) => ({ ...f, color: c }))}
                  sx={{
                    width: { xs: 36, sm: 28 },
                    height: { xs: 36, sm: 28 },
                    borderRadius: "6px",
                    bgcolor: c,
                    cursor: "pointer",
                    border:
                      form.color === c
                        ? lightDark("3px solid #000", "3px solid #fff")
                        : "2px solid transparent",
                    transition: "transform 0.1s",
                    "&:hover": { transform: "scale(1.15)" },
                  }}
                />
              ))}
            </MDBox>
          </MDBox>

          <MDBox display="flex" alignItems="center" gap={1}>
            <input
              type="checkbox"
              id="active-check"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
            />
            {/* py on phones: the label is the checkbox's tap target */}
            <MDBox
              component="label"
              htmlFor="active-check"
              sx={{ fontSize: "0.85rem", py: { xs: 1, sm: 0 } }}
            >
              Active (show in entry form)
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <MDButton
            onClick={() => setDialogOpen(false)}
            color="secondary"
            size="small"
            sx={TAP_BUTTON_SX}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            onClick={handleSave}
            disabled={saving}
            sx={TAP_BUTTON_SX}
          >
            {saving ? (
              <CircularProgress size={14} color="inherit" />
            ) : editProject ? (
              "Update"
            ) : (
              "Create"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

// ─── REPORTS TAB ──────────────────────────────────────────────────────────────

function getInitials(name, email) {
  const src = (name || email || "?").trim();
  const parts = src.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function ReportsTab({ projects }) {
  const today = new Date();
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  const todayStr = toLocalDateStr(today);

  const [filters, setFilters] = useState({
    startDate: firstOfMonth,
    endDate: todayStr,
    projectId: "all",
    allUsers: "false",
  });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);

  const { user } = useAuth();

  useEffect(() => {
    if (user?.isGlobalAdmin) {
      adminApi
        .getUsers({ limit: 200 })
        .then((res) => {
          const list = Array.isArray(res) ? res : res.users || res.data || [];
          setUsers(list);
        })
        .catch(() => {});
    }
  }, [user]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        ...(filters.projectId !== "all" && { projectId: filters.projectId }),
        ...(filters.allUsers === "true" && { allUsers: "true" }),
        ...(filters.allUsers !== "true" &&
          filters.allUsers !== "false" && { userId: filters.allUsers }),
      };
      const data = await adminApi.getTimesheetEntries(params);
      setEntries(data);
    } catch (err) {
      setError("Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, []);

  // Stats
  const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
  const projectBreakdown = {};
  entries.forEach((e) => {
    const key = e.projectName || "Other";
    if (!projectBreakdown[key])
      projectBreakdown[key] = { count: 0, hours: 0, color: e.project?.color || "#667eea" };
    projectBreakdown[key].count++;
    projectBreakdown[key].hours += e.hours || 0;
  });

  const handleExportCSV = () => {
    const headers = ["Date", "End Date", "Project", "Description", "Hours", "User"];
    const rows = entries.map((e) => [
      toLocalDateStr(e.date),
      e.endDate ? toLocalDateStr(e.endDate) : "",
      e.projectName,
      e.description || "",
      e.hours || "",
      e.userName || e.userEmail || "",
    ]);
    // Every field quoted: a name or project containing a comma used to split into two columns.
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timesheet-report-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = async () => {
    try {
      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF({ orientation: "landscape" });

      doc.setFontSize(16);
      doc.text("Timesheet Report", 14, 18);
      doc.setFontSize(10);
      doc.text(`Period: ${filters.startDate} to ${filters.endDate}`, 14, 26);
      doc.text(
        `Total entries: ${entries.length}   Total hours: ${
          totalHours > 0 ? totalHours + "h" : "—"
        }`,
        14,
        32
      );

      const showUser = user?.isGlobalAdmin;
      autoTable(doc, {
        startY: 38,
        head: [
          ["Date", "End Date", "Project", "Description", "Hours", showUser && "User"].filter(
            Boolean
          ),
        ],
        body: entries.map((e) =>
          [
            toLocalDateStr(e.date),
            e.endDate ? toLocalDateStr(e.endDate) : "—",
            e.projectName,
            e.description,
            e.hours ? `${e.hours}h` : "—",
            showUser && (e.userName || e.userEmail || "—"),
          ].filter((_, i) => showUser || i !== 5)
        ),
        headStyles: { fillColor: [102, 126, 234], fontSize: 9 },
        styles: { fontSize: 8.5 },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 28 },
          3: { cellWidth: "auto" },
          4: { cellWidth: 20, halign: "center" },
        },
      });

      doc.save(`timesheet-report-${filters.startDate}-to-${filters.endDate}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <MDBox p={{ xs: 2, sm: 3 }}>
      {/* Filters */}
      <MDBox
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: "12px",
          bgcolor: lightDark("grey.50", DARK.surface),
          border: "1px solid",
          borderColor: lightDark("grey.200", DARK.border),
          mb: 3,
        }}
      >
        <MDTypography
          variant="button"
          fontWeight="bold"
          color="text"
          sx={{ mb: 2, display: "block" }}
        >
          Filter Report
        </MDTypography>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="From date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="To date"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Project</InputLabel>
              <Select
                value={filters.projectId}
                label="Project"
                onChange={(e) => setFilters((f) => ({ ...f, projectId: e.target.value }))}
              >
                <MenuItem value="all">All projects</MenuItem>
                <MenuItem value="other">Other</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p._id} value={p._id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {user?.isGlobalAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Entries for</InputLabel>
                <Select
                  value={filters.allUsers}
                  label="Entries for"
                  onChange={(e) => setFilters((f) => ({ ...f, allUsers: e.target.value }))}
                >
                  <MenuItem value="false">My entries</MenuItem>
                  <MenuItem value="true">All users</MenuItem>
                  {users.length > 0 && <Divider />}
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        {u.profileImage ? (
                          <img
                            src={u.profileImage}
                            alt={u.name}
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              bgcolor: "#667eea",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <MDTypography
                              sx={{ color: "white", fontSize: "0.55rem", fontWeight: 700 }}
                            >
                              {getInitials(u.name, u.email)}
                            </MDTypography>
                          </MDBox>
                        )}
                        {u.name || u.email}
                      </MDBox>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={user?.isGlobalAdmin ? 3 : 3}>
            <MDButton
              variant="gradient"
              color="dark"
              fullWidth
              onClick={fetchReport}
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : "Apply"}
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <MDBox
            sx={{
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}
            >
              Total Entries
            </MDTypography>
            <MDTypography variant="h4" fontWeight="bold" color="white">
              {entries.length}
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDBox
            sx={{
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}
            >
              Total Hours
            </MDTypography>
            <MDTypography variant="h4" fontWeight="bold" color="white">
              {totalHours > 0 ? `${totalHours}h` : "—"}
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDBox
            sx={{
              p: 2,
              borderRadius: "12px",
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            }}
          >
            <MDTypography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.7)", display: "block" }}
            >
              Projects Worked On
            </MDTypography>
            <MDTypography variant="h4" fontWeight="bold" color="white">
              {Object.keys(projectBreakdown).length}
            </MDTypography>
          </MDBox>
        </Grid>
      </Grid>

      {/* Project breakdown */}
      {Object.keys(projectBreakdown).length > 0 && (
        <MDBox mb={3}>
          <MDTypography
            variant="button"
            fontWeight="bold"
            color="text"
            sx={{ mb: 1.5, display: "block" }}
          >
            Breakdown by Project
          </MDTypography>
          <MDBox display="flex" flexWrap="wrap" gap={1}>
            {Object.entries(projectBreakdown).map(([name, data]) => (
              <Chip
                key={name}
                label={`${name}: ${data.count} entr${data.count === 1 ? "y" : "ies"}${
                  data.hours ? ` · ${data.hours}h` : ""
                }`}
                sx={{ bgcolor: data.color, color: "white", fontWeight: 600 }}
              />
            ))}
          </MDBox>
        </MDBox>
      )}

      {/* Export buttons */}
      {entries.length > 0 && (
        <MDBox display="flex" flexWrap="wrap" gap={1} mb={3}>
          <MDButton
            size="small"
            variant="outlined"
            color="dark"
            startIcon={<Icon>download</Icon>}
            onClick={handleExportCSV}
            sx={TAP_BUTTON_SX}
          >
            Export CSV
          </MDButton>
          <MDButton
            size="small"
            variant="outlined"
            color="dark"
            startIcon={<Icon>picture_as_pdf</Icon>}
            onClick={handleExportPDF}
            sx={TAP_BUTTON_SX}
          >
            Export PDF
          </MDButton>
        </MDBox>
      )}

      {/* Entries table */}
      {loading ? (
        <MDBox display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </MDBox>
      ) : entries.length === 0 ? (
        <MDBox textAlign="center" py={6}>
          <Icon sx={{ fontSize: 48, color: "grey.400" }}>event_busy</Icon>
          <MDTypography variant="body2" color="text" mt={1}>
            No entries for the selected period
          </MDTypography>
        </MDBox>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: "12px",
            boxShadow: "none",
            border: "1px solid",
            borderColor: lightDark("grey.200", DARK.border),
          }}
        >
          {/* Below md: keep columns readable and let the container scroll sideways instead of
              squeezing Description into a sliver. The theme's `thead { display: block }` lands the
              whole header row inside the Date column (379px wide, headers misaligned); restore
              table-header-group so headers line up with their columns. md+ keeps the theme value. */}
          <Table
            size="small"
            sx={{
              minWidth: { xs: 640, md: "auto" },
              "& .MuiTableHead-root": { display: { xs: "table-header-group", md: "block" } },
            }}
          >
            <TableHead>
              <TableRow sx={{ bgcolor: lightDark("grey.50", DARK.surface) }}>
                {["Date", "Project", "Description", "Hours", user?.isGlobalAdmin && "User"]
                  .filter(Boolean)
                  .map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e._id} hover>
                  <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                    {formatDate(e.date)}
                    {/* below md the range drops to its own line so Date stays narrow */}
                    {e.endDate && (
                      <MDBox
                        component="span"
                        sx={{
                          color: "#888",
                          fontSize: "0.7rem",
                          display: { xs: "block", md: "inline" },
                        }}
                      >
                        {" "}
                        → {formatDate(e.endDate)}
                      </MDBox>
                    )}
                  </TableCell>
                  <TableCell>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDBox
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "2px",
                          bgcolor: e.project?.color || "#667eea",
                          flexShrink: 0,
                        }}
                      />
                      <MDTypography sx={{ fontSize: "0.8rem" }}>{e.projectName}</MDTypography>
                    </MDBox>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: "0.8rem", maxWidth: 300, minWidth: { xs: 200, md: 0 } }}
                  >
                    {e.description}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem" }}>{e.hours ? `${e.hours}h` : "—"}</TableCell>
                  {user?.isGlobalAdmin && (
                    <TableCell
                      sx={{ fontSize: "0.8rem", color: lightDark("grey.600", DARK.textMuted) }}
                    >
                      {e.userName || e.userEmail || "—"}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </MDBox>
  );
}

// ─── TIMESHEET CARD TAB ───────────────────────────────────────────────────────
function TimesheetCardTab() {
  const { user } = useAuth();
  const today = new Date();

  const [monthly, setMonthly] = useState({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    allUsers: "false",
  });
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState("");
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (user?.isGlobalAdmin) {
      adminApi
        .getUsers({ limit: 200 })
        .then((res) => {
          const list = Array.isArray(res) ? res : res.users || res.data || [];
          setUsers(list);
        })
        .catch(() => {});
    }
  }, [user]);

  const fetchMonthlyEntries = async () => {
    const monthStr = String(monthly.month).padStart(2, "0");
    const lastDay = new Date(monthly.year, monthly.month, 0).getDate();
    const params = {
      startDate: `${monthly.year}-${monthStr}-01`,
      endDate: `${monthly.year}-${monthStr}-${lastDay}`,
      // Include multi-day entries that started last month and run into this one.
      overlap: "true",
      ...(monthly.allUsers === "true" && { allUsers: "true" }),
      ...(monthly.allUsers !== "true" &&
        monthly.allUsers !== "false" && { userId: monthly.allUsers }),
    };
    return adminApi.getTimesheetEntries(params);
  };

  const loadImageBase64 = async (url) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };

  const selectedUser = users.find((u) => u._id === monthly.allUsers) || null;
  const displayName =
    monthly.allUsers === "true"
      ? "All Team Members"
      : monthly.allUsers === "false"
      ? user?.name || user?.email || "Unknown"
      : selectedUser?.name || selectedUser?.email || "Unknown";
  const displayPosition =
    monthly.allUsers === "true"
      ? "Team Report"
      : monthly.allUsers === "false"
      ? user?.position || (user?.isGlobalAdmin ? "Administrator" : "Team Member")
      : selectedUser?.position || "Team Member";
  const displayEmail =
    monthly.allUsers === "true"
      ? null
      : monthly.allUsers === "false"
      ? user?.email
      : selectedUser?.email;
  const displayImage = monthly.allUsers === "false" ? user?.profileImage : null;

  const generateMonthlyPDF = async () => {
    setMonthlyLoading(true);
    setMonthlyError("");
    try {
      const data = await fetchMonthlyEntries();
      const monthStr = String(monthly.month).padStart(2, "0");
      const monthLabel = `${MONTHS[monthly.month - 1]} ${monthly.year}`;
      const lastDay = new Date(monthly.year, monthly.month, 0).getDate();
      const grandTotal = monthTotalHours(data, monthly.year, monthly.month);
      const showUser = monthly.allUsers === "true" && user?.isGlobalAdmin;
      const rows = buildMonthlyRows(data, monthly.year, monthly.month);

      const { jsPDF } = await import("jspdf");
      const { default: autoTable } = await import("jspdf-autotable");
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();

      doc.setFillColor(118, 75, 162);
      doc.rect(0, 0, W, 56, "F");

      const accentStops = [
        [102, 126, 234],
        [107, 109, 210],
        [113, 92, 186],
        [118, 75, 162],
      ];
      const segH = 56 / accentStops.length;
      accentStops.forEach(([r, g, b], i) => {
        doc.setFillColor(r, g, b);
        doc.rect(0, i * segH, 5, segH + 0.5, "F");
      });

      doc.setFillColor(255, 255, 255);
      doc.setGState(doc.GState({ opacity: 0.025 }));
      for (let x = 10; x < W; x += 12) doc.rect(x, 0, 4, 56, "F");
      doc.setGState(doc.GState({ opacity: 1 }));

      const avatarX = 22,
        avatarY = 28,
        avatarR = 14;
      let imageLoaded = false;
      if (displayImage) {
        const b64 = await loadImageBase64(displayImage);
        if (b64) {
          doc.setFillColor(102, 126, 234);
          doc.circle(avatarX, avatarY, avatarR + 2, "F");
          try {
            doc.addImage(
              b64,
              "JPEG",
              avatarX - avatarR,
              avatarY - avatarR,
              avatarR * 2,
              avatarR * 2
            );
            imageLoaded = true;
          } catch {}
        }
      }
      if (!imageLoaded) {
        doc.setFillColor(102, 126, 234);
        doc.setGState(doc.GState({ opacity: 0.35 }));
        doc.circle(avatarX, avatarY, avatarR + 3.5, "F");
        doc.setGState(doc.GState({ opacity: 1 }));
        doc.setFillColor(showUser ? 67 : 102, showUser ? 170 : 126, showUser ? 139 : 234);
        doc.circle(avatarX, avatarY, avatarR + 1.5, "F");
        doc.setFillColor(20, 35, 70);
        doc.circle(avatarX, avatarY, avatarR, "F");
        const initials = showUser ? "ALL" : getInitials(displayName, displayEmail);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(initials.length > 2 ? 9 : 13);
        doc.setFont(undefined, "bold");
        doc.text(initials, avatarX, avatarY + 0.6, { align: "center", baseline: "middle" });
      }

      const infoX = 42;
      doc.setFontSize(13);
      doc.setFont(undefined, "bold");
      doc.setTextColor(255, 255, 255);
      doc.text(displayName, infoX, 19);
      doc.setFontSize(8.5);
      doc.setFont(undefined, "normal");
      doc.setTextColor(180, 195, 230);
      doc.text(displayPosition, infoX, 26.5);
      if (displayEmail) {
        doc.setFontSize(8);
        doc.setTextColor(120, 140, 180);
        doc.text(displayEmail, infoX, 33);
      }
      doc.setFillColor(102, 126, 234);
      doc.setGState(doc.GState({ opacity: 0.15 }));
      doc.roundedRect(infoX - 1, 37, 46, 7, 2, 2, "F");
      doc.setGState(doc.GState({ opacity: 1 }));
      doc.setFontSize(7);
      doc.setFont(undefined, "bold");
      doc.setTextColor(102, 126, 234);
      doc.text("MONTHLY TIMESHEET", infoX + 1.5, 41.8);

      doc.setFontSize(8.5);
      doc.setFont(undefined, "bold");
      doc.setTextColor(200, 190, 225);
      doc.text("ELITE 247 TECH HUB", W - 12, 13, { align: "right" });
      doc.setDrawColor(150, 130, 190);
      doc.setLineWidth(0.3);
      doc.line(W - 75, 16, W - 12, 16);
      doc.setFontSize(24);
      doc.setFont(undefined, "black");
      doc.setTextColor(255, 255, 255);
      doc.text(MONTHS[monthly.month - 1].toUpperCase(), W - 12, 30, { align: "right" });
      doc.setFontSize(13);
      doc.setFont(undefined, "bold");
      doc.setTextColor(102, 126, 234);
      doc.text(String(monthly.year), W - 12, 39, { align: "right" });
      doc.setFontSize(7.5);
      doc.setFont(undefined, "normal");
      doc.setTextColor(190, 175, 215);
      doc.text(
        `${lastDay} days  ·  ${monthEntryCount(data, monthly.year, monthly.month)} entries  ·  ${
          grandTotal > 0 ? grandTotal + "h logged" : "no hours logged"
        }`,
        W - 12,
        47,
        { align: "right" }
      );
      doc.setDrawColor(102, 126, 234);
      doc.setLineWidth(0.6);
      doc.line(5, 56.5, W, 56.5);

      const head = [
        ["Date", "Day", "Project", "Description", "Hours", "Day Total", showUser && "User"].filter(
          Boolean
        ),
      ];
      const body = rows.map((r) =>
        [
          r.dateLabel,
          r.dayName,
          r.entry?.projectName || (r.isFirst ? "—" : ""),
          r.entry?.description || "",
          r.continued ? "(cont.)" : r.entry?.hours ? `${r.entry.hours}h` : r.isFirst ? "—" : "",
          r.isLast && r.dayTotal > 0 ? `${r.dayTotal}h` : "",
          showUser && (r.isFirst ? r.entry?.userName || r.entry?.userEmail || "—" : ""),
        ].filter((_, i) => showUser || i !== 6)
      );

      autoTable(doc, {
        startY: 60,
        head,
        body,
        headStyles: {
          fillColor: [118, 75, 162],
          textColor: [230, 225, 245],
          fontStyle: "bold",
          fontSize: 8.5,
          cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
        },
        styles: { fontSize: 8, cellPadding: 2.5, lineColor: [230, 233, 242], lineWidth: 0.2 },
        alternateRowStyles: { fillColor: [246, 248, 254] },
        columnStyles: {
          0: { cellWidth: 22, fontStyle: "bold", textColor: [80, 60, 130] },
          1: { cellWidth: 13, textColor: [120, 130, 170] },
          2: { cellWidth: 42 },
          3: { cellWidth: "auto" },
          4: { cellWidth: 17, halign: "center" },
          5: { cellWidth: 24, halign: "center", fontStyle: "bold", textColor: [102, 126, 234] },
        },
        didParseCell: (hookData) => {
          const row = rows[hookData.row.index];
          if (row?.isFirst && hookData.row.index > 0) {
            hookData.cell.styles.lineWidths = { top: 0.5 };
            hookData.cell.styles.lineColor = [210, 215, 235];
          }
          if (!row?.entry && hookData.column.index >= 2)
            hookData.cell.styles.textColor = [200, 205, 220];
        },
        foot: [
          [
            {
              content: `Total: ${grandTotal > 0 ? grandTotal + "h" : "—"}`,
              colSpan: head[0].length,
              styles: {
                halign: "right",
                fontStyle: "bold",
                fillColor: [118, 75, 162],
                textColor: [255, 255, 255],
                fontSize: 9,
              },
            },
          ],
        ],
        footStyles: { fillColor: [118, 75, 162] },
        rowPageBreak: "avoid",
        didDrawPage: (hookData) => {
          doc.setFontSize(7);
          doc.setTextColor(160, 170, 200);
          doc.text(
            `${monthLabel}  ·  ${displayName}  ·  Page ${hookData.pageNumber}`,
            W / 2,
            H - 6,
            { align: "center" }
          );
          doc.setDrawColor(60, 80, 130);
          doc.setLineWidth(0.2);
          doc.line(12, H - 8, W - 12, H - 8);
        },
      });

      doc.save(`monthly-timesheet-${monthly.year}-${monthStr}.pdf`);
    } catch (err) {
      console.error("Monthly PDF failed:", err);
      setMonthlyError("Failed to generate PDF");
    } finally {
      setMonthlyLoading(false);
    }
  };

  const generateMonthlyExcel = async () => {
    setMonthlyLoading(true);
    setMonthlyError("");
    try {
      const data = await fetchMonthlyEntries();
      const monthStr = String(monthly.month).padStart(2, "0");
      const monthLabel = `${MONTHS[monthly.month - 1]} ${monthly.year}`;
      const lastDay = new Date(monthly.year, monthly.month, 0).getDate();
      const grandTotal = monthTotalHours(data, monthly.year, monthly.month);
      const showUser = monthly.allUsers === "true" && user?.isGlobalAdmin;
      const rows = buildMonthlyRows(data, monthly.year, monthly.month);

      const { utils, writeFile } = await import("xlsx");
      const colCount = showUser ? 7 : 6;

      const sheetRows = [
        ["ELITE 247 TECH HUB"],
        ["Monthly Timesheet"],
        [],
        ["Employee:", displayName],
        ["Position:", displayPosition],
        ["Email:", displayEmail || "—"],
        ["Period:", monthLabel],
        ["Total Days:", lastDay],
        ["Total Entries:", monthEntryCount(data, monthly.year, monthly.month)],
        ["Total Hours:", grandTotal > 0 ? grandTotal : "—"],
        [],
        ["Date", "Day", "Project", "Description", "Hours", "Day Total", showUser && "User"].filter(
          Boolean
        ),
        ...rows.map((r) =>
          [
            r.dateLabel,
            r.dayName,
            r.entry?.projectName || (r.isFirst ? "—" : ""),
            r.entry?.description || "",
            r.continued ? "(cont.)" : r.entry?.hours || "",
            r.isLast && r.dayTotal > 0 ? r.dayTotal : "",
            showUser && (r.isFirst ? r.entry?.userName || r.entry?.userEmail || "—" : ""),
          ].filter((_, i) => showUser || i !== 6)
        ),
        [],
        [`TOTAL HOURS: ${grandTotal > 0 ? grandTotal + "h" : "—"}`],
        [],
        [
          `Generated: ${new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}`,
        ],
      ];

      const ws = utils.aoa_to_sheet(sheetRows);
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
        { s: { r: sheetRows.length - 3, c: 0 }, e: { r: sheetRows.length - 3, c: colCount - 1 } },
        { s: { r: sheetRows.length - 1, c: 0 }, e: { r: sheetRows.length - 1, c: colCount - 1 } },
      ];
      ws["!cols"] = [
        { wch: 12 },
        { wch: 8 },
        { wch: 28 },
        { wch: 58 },
        { wch: 8 },
        { wch: 12 },
        ...(showUser ? [{ wch: 28 }] : []),
      ];

      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, monthLabel.slice(0, 31));
      writeFile(wb, `monthly-timesheet-${monthly.year}-${monthStr}.xlsx`);
    } catch (err) {
      console.error("Monthly Excel failed:", err);
      setMonthlyError("Failed to generate Excel file");
    } finally {
      setMonthlyLoading(false);
    }
  };

  return (
    <MDBox p={{ xs: 2, sm: 3 }}>
      {/* ── Outer card ── */}
      <MDBox
        sx={{
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(102,126,234,0.35)",
          border: "1px solid rgba(102,126,234,0.15)",
        }}
      >
        {/* Header bar */}
        <MDBox
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            borderBottom: "1px solid rgba(102,126,234,0.2)",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1.5}>
            <MDBox
              sx={{
                width: 34,
                height: 34,
                borderRadius: "9px",
                background: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color: "white", fontSize: 18 }}>calendar_view_month</Icon>
            </MDBox>
            <MDBox>
              <MDTypography
                variant="button"
                fontWeight="bold"
                color="white"
                sx={{ display: "block", lineHeight: 1.2 }}
              >
                Monthly Timesheet
              </MDTypography>
              <MDTypography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.68rem" }}
              >
                Full month · every day listed
              </MDTypography>
            </MDBox>
          </MDBox>
          <Chip
            label="PDF  &  Excel"
            size="small"
            sx={{
              bgcolor: "rgba(102,126,234,0.12)",
              color: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(102,126,234,0.25)",
              fontSize: "0.65rem",
              fontWeight: 700,
            }}
          />
        </MDBox>

        {/* Document preview */}
        <MDBox
          sx={{
            background: "linear-gradient(160deg, #1a1530 0%, #241c3d 100%)",
            p: { xs: 2, sm: 3 },
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              backgroundImage: "radial-gradient(rgba(102,126,234,0.07) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            },
          }}
        >
          <MDBox
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: "1px solid rgba(102,126,234,0.18)",
              boxShadow: "0 12px 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
              background: "linear-gradient(160deg, #667eea 0%, #764ba2 100%)",
              position: "relative",
              zIndex: 1,
            }}
          >
            <MDBox
              sx={{ height: 5, background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)" }}
            />
            <MDBox
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, sm: 3.5 },
                flexWrap: "wrap",
              }}
            >
              {/* Avatar */}
              <MDBox
                sx={{
                  width: { xs: 64, sm: 80 },
                  height: { xs: 64, sm: 80 },
                  borderRadius: "50%",
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: -4,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.4)",
                    opacity: 0.45,
                    filter: "blur(6px)",
                  }}
                />
                <MDBox
                  sx={{
                    position: "absolute",
                    inset: -2.5,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.5)",
                  }}
                />
                <MDBox
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "2.5px solid #764ba2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.15)",
                  }}
                >
                  {displayImage ? (
                    <img
                      src={displayImage}
                      alt={displayName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <MDTypography
                      fontWeight="black"
                      sx={{
                        color: "white",
                        fontSize: { xs: "1.1rem", sm: "1.4rem" },
                        letterSpacing: "-0.5px",
                        lineHeight: 1,
                        userSelect: "none",
                      }}
                    >
                      {monthly.allUsers === "true" && user?.isGlobalAdmin
                        ? "ALL"
                        : getInitials(displayName, displayEmail)}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>

              {/* User info — below md, minWidth makes the Month/Year block wrap onto its
                  own row instead of squeezing the name into one letter per line */}
              <MDBox flex={1} minWidth={{ xs: 140, md: 0 }}>
                <MDTypography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    color: "white",
                    lineHeight: 1.15,
                    mb: 0.5,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                  }}
                >
                  {displayName}
                </MDTypography>
                {displayEmail && (
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.45)",
                      display: "block",
                      mb: 1,
                      fontSize: "0.75rem",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {displayEmail}
                  </MDTypography>
                )}
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    label={displayPosition}
                    size="small"
                    sx={{
                      bgcolor: "rgba(102,126,234,0.15)",
                      color: "#a0aeff",
                      border: "1px solid rgba(102,126,234,0.3)",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      height: 22,
                    }}
                  />
                  <MDBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      bgcolor: "rgba(255,255,255,0.15)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: "100px",
                      px: 1.2,
                      py: 0.3,
                    }}
                  >
                    <Icon sx={{ fontSize: "0.8rem !important", color: "white" }}>
                      calendar_today
                    </Icon>
                    <MDTypography sx={{ fontSize: "0.65rem", color: "white", fontWeight: 700 }}>
                      {new Date(monthly.year, monthly.month, 0).getDate()} days
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>

              {/* Month/Year */}
              <MDBox sx={{ textAlign: "right", flexShrink: 0, ml: { xs: "auto", md: 0 } }}>
                <MDTypography
                  fontWeight="black"
                  sx={{
                    color: "white",
                    fontSize: { xs: "1.6rem", sm: "2.2rem" },
                    lineHeight: 1,
                    letterSpacing: "-1px",
                  }}
                >
                  {MONTHS[monthly.month - 1]}
                </MDTypography>
                <MDTypography
                  fontWeight="bold"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {monthly.year}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.25)",
                    fontSize: "0.62rem",
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                  }}
                >
                  Timesheet
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Card footer */}
            <MDBox
              sx={{
                px: { xs: 2.5, sm: 3.5 },
                py: 1.5,
                background: "rgba(0,0,0,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 0.5,
                borderTop: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <MDTypography
                sx={{
                  color: "rgba(255,255,255,0.3)",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 2,
                }}
              >
                Elite 247 Tech Hub
              </MDTypography>
              <MDTypography sx={{ color: "rgba(255,255,255,0.2)", fontSize: "0.6rem" }}>
                Monthly Timesheet System
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>

        {/* Controls */}
        <MDBox
          sx={{
            bgcolor: lightDark("#f8f9fc", DARK.surface),
            borderTop: "1px solid rgba(102,126,234,0.12)",
            p: { xs: 2, sm: 3 },
          }}
        >
          {monthlyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {monthlyError}
            </Alert>
          )}

          {/* Month navigator */}
          <MDBox display="flex" alignItems="center" justifyContent="center" mb={3}>
            <IconButton
              onClick={() =>
                setMonthly((m) => {
                  const d = new Date(m.year, m.month - 2, 1);
                  return { ...m, month: d.getMonth() + 1, year: d.getFullYear() };
                })
              }
              sx={{
                bgcolor: lightDark("white", DARK.surface),
                border: "1.5px solid",
                borderColor: lightDark("grey.200", DARK.border),
                "&:hover": {
                  bgcolor: lightDark("grey.100", DARK.surfaceHover),
                  borderColor: "primary.main",
                },
              }}
            >
              <Icon>chevron_left</Icon>
            </IconButton>
            <MDTypography
              fontWeight="bold"
              sx={{
                mx: 3,
                minWidth: 130,
                textAlign: "center",
                color: lightDark("#344767", DARK.text),
                fontSize: "1.05rem",
              }}
            >
              {MONTHS[monthly.month - 1]} {monthly.year}
            </MDTypography>
            <IconButton
              onClick={() =>
                setMonthly((m) => {
                  const d = new Date(m.year, m.month, 1);
                  return { ...m, month: d.getMonth() + 1, year: d.getFullYear() };
                })
              }
              sx={{
                bgcolor: lightDark("white", DARK.surface),
                border: "1.5px solid",
                borderColor: lightDark("grey.200", DARK.border),
                "&:hover": {
                  bgcolor: lightDark("grey.100", DARK.surfaceHover),
                  borderColor: "primary.main",
                },
              }}
            >
              <Icon>chevron_right</Icon>
            </IconButton>
          </MDBox>

          {/* User selector (admin only) */}
          {user?.isGlobalAdmin && (
            <MDBox mb={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Whose timesheet</InputLabel>
                <Select
                  value={monthly.allUsers}
                  label="Whose timesheet"
                  onChange={(e) => setMonthly((m) => ({ ...m, allUsers: e.target.value }))}
                >
                  <MenuItem value="false">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ fontSize: "1rem", color: "#667eea" }}>person</Icon>
                      My timesheet
                    </MDBox>
                  </MenuItem>
                  <MenuItem value="true">
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ fontSize: "1rem", color: "primary.main" }}>group</Icon>
                      All users
                    </MDBox>
                  </MenuItem>
                  {users.length > 0 && <Divider />}
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        {u.profileImage ? (
                          <img
                            src={u.profileImage}
                            alt={u.name}
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <MDBox
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              bgcolor: "#667eea",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <MDTypography
                              sx={{ color: "white", fontSize: "0.6rem", fontWeight: 700 }}
                            >
                              {getInitials(u.name, u.email)}
                            </MDTypography>
                          </MDBox>
                        )}
                        <MDBox>
                          <MDTypography sx={{ fontSize: "0.85rem", lineHeight: 1.2 }}>
                            {u.name || u.email}
                          </MDTypography>
                          {u.position && (
                            <MDTypography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>
                              {u.position}
                            </MDTypography>
                          )}
                        </MDBox>
                      </MDBox>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </MDBox>
          )}

          {/* Download buttons */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <MDButton
                fullWidth
                variant="contained"
                startIcon={
                  monthlyLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon>picture_as_pdf</Icon>
                  )
                }
                onClick={generateMonthlyPDF}
                disabled={monthlyLoading}
                sx={{
                  py: 1.6,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  boxShadow: "0 6px 20px rgba(102,126,234,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5568d3 0%, #623d89 100%)",
                    boxShadow: "0 8px 28px rgba(102,126,234,0.5)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": { opacity: 0.5 },
                  transition: "all 0.2s ease",
                }}
              >
                Download PDF
              </MDButton>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDButton
                fullWidth
                variant="contained"
                startIcon={
                  monthlyLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Icon>table_view</Icon>
                  )
                }
                onClick={generateMonthlyExcel}
                disabled={monthlyLoading}
                sx={{
                  py: 1.6,
                  background: "linear-gradient(135deg, #1D6F42 0%, #155030 100%)",
                  color: "white",
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  boxShadow: "0 6px 20px rgba(29,111,66,0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #155534 0%, #0e3d24 100%)",
                    boxShadow: "0 8px 28px rgba(29,111,66,0.5)",
                    transform: "translateY(-1px)",
                  },
                  "&.Mui-disabled": { opacity: 0.5 },
                  transition: "all 0.2s ease",
                }}
              >
                Download Excel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

// ─── MAIN PAGE ─────────────────────────────────────────────────────────────────
function Timesheet() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(true);
  // The month shown on the Calendar tab (month 0-11); its entries load whenever it changes.
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  // Only the newest load may fill the calendar: paging quickly must not let an older reply land last.
  const calendarRequest = useRef(0);

  // Admin-only: who the Calendar tab is currently showing/adding entries
  // for. null means "me" — every non-admin stays on this path permanently,
  // since the picker that would ever change it only renders for admins.
  const [targetUserId, setTargetUserId] = useState(null);
  const [calendarUsers, setCalendarUsers] = useState([]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDayEntries, setSelectedDayEntries] = useState([]);

  // Load projects (needed by all tabs)
  const fetchProjects = useCallback(async () => {
    try {
      const data = await adminApi.getTimesheetProjects();
      setProjects(data);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    }
  }, []);

  // Load the user list for the admin-only Calendar tab picker
  useEffect(() => {
    if (user?.isGlobalAdmin) {
      adminApi
        .getUsers({ limit: 200 })
        .then((res) => {
          const list = Array.isArray(res) ? res : res.users || res.data || [];
          setCalendarUsers(list);
        })
        .catch(() => {});
    }
  }, [user]);

  // Load entries for the shown month and one either side (calendar), including multi-day
  // entries that started earlier and run into it — scoped to targetUserId when an admin has
  // picked someone other than themselves. Re-runs whenever the calendar changes month.
  const fetchCalendarEntries = useCallback(async () => {
    calendarRequest.current += 1;
    const requestId = calendarRequest.current;
    setCalendarLoading(true);
    try {
      const start = new Date(calendarMonth.year, calendarMonth.month - 1, 1);
      const end = new Date(calendarMonth.year, calendarMonth.month + 2, 0);
      const params = {
        startDate: toLocalDateStr(start),
        endDate: toLocalDateStr(end),
        overlap: "true",
      };
      if (targetUserId) params.userId = targetUserId;
      const data = await adminApi.getTimesheetEntries(params);
      if (calendarRequest.current === requestId) setEntries(data);
    } catch (err) {
      console.error("Failed to fetch entries:", err);
    } finally {
      if (calendarRequest.current === requestId) setCalendarLoading(false);
    }
  }, [targetUserId, calendarMonth]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchCalendarEntries();
  }, [fetchCalendarEntries]);

  const handleDayClick = (date, dayEntries) => {
    setSelectedDate(date);
    setSelectedDayEntries(dayEntries);
    setDialogOpen(true);
  };

  const handleEntrySaved = () => {
    fetchCalendarEntries();
    // refresh selected day entries
    setDialogOpen(false);
    setTimeout(() => {
      fetchCalendarEntries();
    }, 300);
  };

  const handleEntryDeleted = () => {
    fetchCalendarEntries();
    setDialogOpen(false);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "0 8px 32px rgba(102,126,234,0.12)",
                border: "1px solid rgba(102,126,234,0.08)",
              }}
            >
              <PageHeader activeTab={activeTab} onTabChange={setActiveTab} />

              <MDBox>
                {activeTab === 0 && (
                  <CalendarTab
                    entries={entries}
                    projects={projects}
                    onDayClick={handleDayClick}
                    loading={calendarLoading}
                    isGlobalAdmin={!!user?.isGlobalAdmin}
                    users={calendarUsers}
                    targetUserId={targetUserId}
                    onTargetUserChange={setTargetUserId}
                    year={calendarMonth.year}
                    month={calendarMonth.month}
                    onMonthChange={(year, month) => setCalendarMonth({ year, month })}
                  />
                )}
                {activeTab === 1 && <ProjectsTab projects={projects} onRefresh={fetchProjects} />}
                {activeTab === 2 && <ReportsTab projects={projects} />}
                {activeTab === 3 && <TimesheetCardTab />}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <EntryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        date={selectedDate}
        dayEntries={selectedDayEntries}
        projects={projects}
        onSaved={handleEntrySaved}
        onDeleted={handleEntryDeleted}
        targetUserId={targetUserId}
        targetUserName={
          targetUserId
            ? calendarUsers.find((u) => u._id === targetUserId)?.name ||
              calendarUsers.find((u) => u._id === targetUserId)?.email ||
              ""
            : ""
        }
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Timesheet;
